<template>
  <div
    v-if="getIsClient() && (isLoading || (!content.length && status === 'idle'))"
    class="flex flex-col"
  >
    <LcSkeleton height="30rem" radius="medium" />
    <LcSkeleton class="mt-200 mb-100" height="1.625rem" radius="medium" width="40%" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" width="40%" />
  </div>

  <div v-else class="info-prizes">
    <PrizeTierTabs
      v-if="tabs.length > 0 && tierTableProps"
      class="mb-300"
      v-bind="{ ...tierTableProps, tabs }"
    />

    <section v-if="content.length">
      <template v-for="item in content" :key="item?.sys.id">
        <h2 v-if="item.caption" class="headline-h4-headline mb-100">
          {{ item.caption }}
        </h2>

        <ContentfulRenderer class="content" :data="item.content" />
      </template>
    </section>
    <IllustratedSection v-else empty illustration="no-result">
      <template #headline>{{ $t('info.noresults.headline') }}</template>
      <template #subtitle>{{ $t('info.noresults.subline') }}</template>

      <LcButton @click="() => execute()">{{ $t('info.noresults.button') }}</LcButton>
    </IllustratedSection>
  </div>
</template>

<script lang="ts" setup>
import type { ContentBlock } from '#content/shared/@types/generated/cms/graphql-schema-types'
import type { PrizeTierTabsProps } from '#product/app/components/PrizeTierTabs.vue'

import {
  type ByAddOnSelected,
  type ByMultiplierDeselected,
  type ByMultiplierSelected,
  type ByMultiplierValueMatched,
  type PrizeTierCategoryRequirements,
  PrizeTierCategoryRequirementsPayloadType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { InfoPageProps } from '../types'

const hasAddOnName = (
  requirement: PrizeTierCategoryRequirements,
): requirement is
  | ByAddOnSelected
  | ByMultiplierDeselected
  | ByMultiplierSelected
  | ByMultiplierValueMatched =>
  [
    PrizeTierCategoryRequirementsPayloadType.ByAddonSelected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierDeselected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierSelected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierValueMatched,
  ].includes(requirement.type)

const isMultiplierRequirement = (
  requirement: PrizeTierCategoryRequirements,
): requirement is ByMultiplierSelected | ByMultiplierValueMatched =>
  [
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierSelected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierValueMatched,
  ].includes(requirement.type)

const { id, isVisible } = defineProps<InfoPageProps>()

const { t } = useI18n()
let playSlip: Maybe<ReturnType<typeof usePlaySlip>> = null

try {
  playSlip = usePlaySlip()
} catch {
  playSlip = null
}

const { data, execute, pending, status } = await useContent().getPrizes(id, {
  immediate: false,
})
const isLoading = useMinimumLoading(pending)

const addOn = computed(() => {
  if (!playSlip) return null
  const { addOns, afterPurchaseAddOns } = playSlip

  return [...addOns.value, ...afterPurchaseAddOns.value].find(isNotPerPlay) ?? null
})
const content = computed(() => (data.value?.contentBlocksCollection?.items as ContentBlock[]) || [])
const prizeTierCategories = computed(() => playSlip?.config.value?.prizeTierCategories ?? [])
const tabs = computed(() =>
  prizeTierCategories.value
    .filter(({ group }) => group === 'DEFAULT')
    .map(({ categoryRequirements, name, prizeTiers }, index) => ({
      data: {
        addOnName: hasAddOnName(categoryRequirements[0])
          ? categoryRequirements[0].addOnName
          : addOn.value?.name,
        prizeTiers,
        tierMultiplier: tierMultipliers.value[index],
      },
      name,
    })),
)
const tierMultipliers = computed(() =>
  prizeTierCategories.value.reduce<string[]>((acc, { categoryRequirements, name }, index) => {
    if (name === 'DEFAULT') return [...acc, '']

    let values: string[] = []

    if (addOn.value?.options.length) {
      values.push(addOn.value.options[index - 1])
    } else {
      values = categoryRequirements
        .filter(isMultiplierRequirement)
        .map(({ value }) => String(value))
    }

    return [...acc, ...values]
  }, []),
)
const tierTableProps = computed<Maybe<Omit<PrizeTierTabsProps, 'tabs'>>>(() => {
  if (!playSlip) return null
  const { addOns, licensedTerritory, lotteryId } = playSlip

  return {
    addOns: addOns.value,
    licensedTerritory,
    lotteryId,
  }
})

watchImmediate(
  () => isVisible,
  async (newValue) => {
    if (!newValue || data.value) return
    await execute()
  },
)

onServerPrefetch(execute)
</script>

<style lang="scss" scoped>
@use '#product/app/components/prize-content' as *;

.info-prizes {
  @include prize-content;
}
</style>
